import { type ReactNode } from "react";
import { LazyMotion, domAnimation, m, useReducedMotion, type Variants } from "framer-motion";
import { cn } from "@/lib/utils";

/** Loads only the DOM animation feature set (~tree-shaken) once for the whole app. */
export const MotionProvider = ({ children }: { children: ReactNode }) => (
  <LazyMotion features={domAnimation} strict>
    {children}
  </LazyMotion>
);

export const EASE_OUT_QUINT: [number, number, number, number] = [0.23, 1, 0.32, 1];

const VIEWPORT = { once: true, margin: "0px 0px -80px 0px" } as const;

interface RevealProps {
  children: ReactNode;
  className?: string;
  /** Delay in seconds. */
  delay?: number;
  /** Vertical offset in px the element travels from. */
  y?: number;
  duration?: number;
  as?: "div" | "section" | "article" | "li" | "span";
}

/** Fades + lifts content in when it scrolls into view. Renders instantly for reduced-motion users. */
export const Reveal = ({ children, className, delay = 0, y = 22, duration = 0.55, as = "div" }: RevealProps) => {
  const reduce = useReducedMotion();
  const Component = m[as];
  return (
    <Component
      className={className}
      initial={reduce ? false : { opacity: 0, y }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={VIEWPORT}
      transition={{ duration, delay, ease: EASE_OUT_QUINT }}
    >
      {children}
    </Component>
  );
};

const staggerContainer: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
};

const staggerItem: Variants = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE_OUT_QUINT } },
};

interface StaggerProps {
  children: ReactNode;
  className?: string;
  as?: "div" | "ul" | "ol";
}

/** Container that staggers its `StaggerItem` children as it enters the viewport. */
export const Stagger = ({ children, className, as = "div" }: StaggerProps) => {
  const reduce = useReducedMotion();
  const Component = m[as];
  return (
    <Component
      className={className}
      variants={staggerContainer}
      initial={reduce ? false : "hidden"}
      whileInView="show"
      viewport={VIEWPORT}
    >
      {children}
    </Component>
  );
};

interface StaggerItemProps {
  children: ReactNode;
  className?: string;
  as?: "div" | "li" | "article";
}

export const StaggerItem = ({ children, className, as = "div" }: StaggerItemProps) => {
  const Component = m[as];
  return (
    <Component className={className} variants={staggerItem}>
      {children}
    </Component>
  );
};

interface FloatProps {
  children: ReactNode;
  className?: string;
  /** Amplitude in px. */
  amplitude?: number;
  duration?: number;
  delay?: number;
}

/** Gentle vertical float loop for decorative cards. Static under reduced motion. */
export const Float = ({ children, className, amplitude = 6, duration = 6, delay = 0 }: FloatProps) => {
  const reduce = useReducedMotion();
  return (
    <m.div
      className={cn("will-change-transform", className)}
      animate={reduce ? undefined : { y: [0, -amplitude, 0] }}
      transition={{ duration, delay, repeat: Infinity, ease: "easeInOut" }}
    >
      {children}
    </m.div>
  );
};

export { m, useReducedMotion };
